/**
 * 065: every sentence the booking form uses to state the outcome of a **tender** the renter
 * supplied — a voucher code or a gift-certificate code.
 *
 * ## Why this is not in `components/ui/resultWording.ts`
 *
 * That module is the single source for stating that **a read did not produce content** — gone,
 * refused, session ended, could not be loaded — and it is shaped around the two render paths that
 * need those words (`ResultState` for a primary read, inline for a supporting lookup).
 *
 * A refused voucher is none of those. The read **succeeded**: a 200 with a fully priced quote, in
 * which the platform returned a verdict about input the renter typed. Filing that under
 * read-failure wording would make `resultWording.ts` mean two unrelated things, and a screen
 * picking a sentence out of it could no longer tell which family it was in.
 *
 * 064's FR-011 asks for **one home per family of wording**, not one home for all wording. This is
 * the second family, and it is bounded: tender verdicts on the booking form. It lives under
 * `pages/booking/` rather than `components/ui/` because both consumers are on this one screen and
 * nothing else in the app has a tender.
 *
 * ## Why the gift-certificate map is here too
 *
 * It used to live in `GiftCertStep.tsx`. The two tenders sit one control apart on the same screen,
 * so leaving one map beside the other's would recreate exactly the divergence 064 found across six
 * screens. The five gift-certificate sentences below are reproduced **byte-for-byte**;
 * `GiftCertStep.test.tsx` asserts them as exact strings and is not edited by this feature, so it is
 * the gate proving the move changed nothing.
 *
 * ## Where the voucher sentences come from
 *
 * The quote payload carries a reason **code**, never a sentence. But the same nine causes are
 * stated a second time if the renter submits anyway: `POST /bookings` re-runs the identical
 * eligibility check and rejects with the backend's own message
 * (`backend/src/modules/discounts/eligibility.ts:56-65` via
 * `backend/src/modules/bookings/service.ts:172`, and `service.ts:153` for an unknown code). So each
 * sentence below is that message with the noun `discount` replaced by `voucher`, because the input
 * the renter filled in is labelled "Voucher Code". Two messages describe one cause; they must not
 * describe it differently.
 *
 * This is a wording alignment, not a shared implementation — no import crosses the service
 * boundary, and the eligibility logic stays in exactly one place.
 */

/** Stated when a voucher was applied — including when it discounted nothing visible (FR-008). */
export const VOUCHER_APPLIED = 'Voucher applied.';

/** Stated for a refusal whose cause this app does not recognise, or that carried none (FR-004). */
const VOUCHER_FALLBACK = 'This voucher code could not be applied.';

/**
 * The ten causes `POST /pricing/quote` can report for a refused voucher: `invalid_code`
 * (`pricing/routes.ts:173`), the eight members of `EligibilityReason`
 * (`discounts/eligibility.ts:9-17`), and `code_used_by_you` (076).
 *
 * `code_used_by_you` is the only one of the ten the platform sends **exclusively to the person it
 * is about**. The same code entered by anyone else — or by a caller the platform has not
 * identified — answers `invalid_code`, both because "you have already used this" would be false
 * about them and because a distinct answer would disclose that the code exists. So the sentence can
 * safely be second-person: there is no reader it could reach for whom it is untrue.
 *
 * Deliberately keyed by `string` rather than a literal union. The wire type is `reason?: string`,
 * so a union here would be a stricter claim than the payload makes — it would let the compiler
 * treat a case analysis as exhaustive while the backend is free to add a tenth code. The closed
 * vocabulary lives in these keys, and anything absent falls through to the fallback.
 */
const VOUCHER_REASON_LABEL: Record<string, string> = {
  invalid_code: 'No voucher found for that code. It may be invalid or expired.',
  inactive: 'This voucher is not active.',
  not_in_validity_window: 'This voucher is not valid at this time.',
  global_limit_reached: 'This voucher has reached its redemption limit.',
  per_user_limit_reached: 'You have already used this voucher the maximum number of times.',
  // 076. Distinct from `per_user_limit_reached` above: that one says the allowance is exhausted,
  // this one says this particular code is spent — a customer with redemptions left still gets this.
  code_used_by_you: 'You have already used this voucher code.',
  car_not_eligible: 'This voucher does not apply to the selected car.',
  rental_window_not_met: 'This voucher is not valid for the selected rental dates.',
  min_amount_not_met: 'The booking total is below the minimum for this voucher.',
  // The second "discount" is not a missed substitution: the thing being conflicted with is a
  // personal (customer-scope) discount, a different object from the voucher.
  non_combinable_conflict: 'This voucher cannot be combined with another discount on this booking.',
};

/**
 * State why a voucher code was not applied.
 *
 * @param reason the code from the quote's `voucher.reason`, or `undefined` if it carried none
 * @returns a complete sentence — never an empty string, whatever it is given
 */
export function voucherReasonMessage(reason?: string): string {
  return (reason && VOUCHER_REASON_LABEL[reason]) || VOUCHER_FALLBACK;
}

/**
 * The gift-certificate causes, from `GET /gift-certificates/validate`.
 *
 * Moved verbatim from `GiftCertStep.tsx`, where it was declared as `REASON_LABEL`. A different
 * vocabulary from the voucher's, and intentionally so — `depleted` is meaningless for a voucher and
 * `car_not_eligible` is meaningless for a gift certificate, so the two maps must never answer for
 * each other. That is why this is two functions and not one generic `tenderMessage(kind, reason)`.
 */
const GIFT_CERT_REASON_LABEL: Record<string, string> = {
  // 358 (FR-005): customers read "credit code"; a code someone else owns answers exactly as an unknown one (FR-018).
  not_found: 'No credit found for that code.',
  revoked: 'This credit code has been cancelled.',
  depleted: 'This credit code has no balance left.',
  expired: 'This credit code has expired.',
  zero_balance: 'This credit code has no balance left.',
  email_not_verified: 'Verify your email to use credit sent to it.',
};

/** State why a credit code cannot be used. */
export function giftCertReasonMessage(reason?: string): string {
  return (reason && GIFT_CERT_REASON_LABEL[reason]) || 'This credit code cannot be used.';
}

/**
 * 358: why "Use my credit" drew nothing, from the quote's `credit.reason`. Separate from the code path's map because
 * the causes differ: credit is never "not found", and a code is never "no credit".
 */
const CREDIT_REASON_LABEL: Record<string, string> = {
  no_credit: 'You have no credit to use.',
  email_not_verified: 'Verify your email to use credit sent to it.',
};

/** State why the customer's credit was not applied. */
export function creditReasonMessage(reason?: string): string {
  return (reason && CREDIT_REASON_LABEL[reason]) || 'Your credit cannot be used for this booking.';
}
